import { useState } from 'react'
import {
  Card,
  CardActions,
  CardContent,
  Chip,
  Typography,
  Collapse,
  IconButtonProps,
  IconButton,
  Grid2
} from '@mui/material'
import { styled } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Button from 'components/Button'
import { useStyles } from './styles'

interface ProfileBankAccountItem {
  id: string
  accountNumber?: string | null
  accountName?: string | null
  active: boolean
  comment?: string | null
  status: 'processing' | 'approved' | 'rejected'
}

interface Props {
  account: ProfileBankAccountItem
  onSetActive: (id: number) => void
  onRemove: (id: number) => void
  loadingArchiveId: number | null
  loadingSetActiveId: number | null
}

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { expand, ...other } = props
  return <IconButton sx={{ p: 0 }} {...other} />
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest
  })
}))

export default function BankAccountCard({
  account,
  onSetActive,
  onRemove,
  loadingArchiveId,
  loadingSetActiveId
}: Readonly<Props>): JSX.Element {
  const { classes } = useStyles()
  const [isExpanded, setIsExpanded] = useState(false)
  const idNum = parseInt(account.id, 10)
  const isApproved = account.status === 'approved'
  const isActive = account.active
  const isRejected = account.status === 'rejected'
  const isPending = account.status === 'processing'
  const showSetActiveAndRemove = isApproved && !isActive
  const showRemoveOnly = isRejected || isPending

  return (
    <Card
      className={`${classes.accountCard} ${
        isRejected || isPending ? classes.accountCardRejectedAndProcessing : ''
      }`}
    >
      <CardContent className={classes.cardContent}>
        <Grid2 container alignItems='center' spacing={1}>
          <Grid2 size={{ xs: 9 }}>
            <Typography variant='h6'>{account.accountNumber ?? '—'}</Typography>
            <Typography variant='subtitle1' className={classes.accountName}>
              {account.accountName ?? '—'}
            </Typography>
          </Grid2>
          <Grid2
            container
            size={{ xs: 3 }}
            justifyContent='flex-end'
            alignItems='center'
          >
            {isApproved && isActive ? (
              <Chip
                size='small'
                color='primary'
                label='Active'
                variant='filled'
                className={`${classes.statusChip}`}
              />
            ) : (
              (isRejected || isPending) && (
                <Chip
                  size='small'
                  color='primary'
                  label={isRejected ? 'Rejected' : 'Pending'}
                  variant='outlined'
                  className={`${classes.statusChip}`}
                />
              )
            )}
            {(showSetActiveAndRemove || showRemoveOnly) && (
              <ExpandMore
                expand={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                aria-label='show actions'
              >
                <ExpandMoreIcon />
              </ExpandMore>
            )}
          </Grid2>
          {isRejected && account.comment && (
            <Grid2 size={{ xs: 12 }}>
              <Typography
                variant='body2'
                color='textSecondary'
                sx={{ mt: 1.5 }}
              >
                Your bank account was rejected for the following reason:{' '}
                {account.comment}
              </Typography>
            </Grid2>
          )}
        </Grid2>
      </CardContent>

      {(showSetActiveAndRemove || showRemoveOnly) && (
        <Collapse in={isExpanded}>
          <CardActions className={classes.cardActions}>
            <Button
              size='small'
              variant='outlined'
              onClick={() => onRemove(idNum)}
              disabled={loadingArchiveId !== null}
              loading={loadingArchiveId === idNum}
            >
              Delete Account
            </Button>
            {showSetActiveAndRemove && (
              <Button
                size='small'
                color='primary'
                variant='contained'
                onClick={() => onSetActive(idNum)}
                disabled={loadingSetActiveId !== null}
                loading={loadingSetActiveId === idNum}
              >
                Set Active
              </Button>
            )}
          </CardActions>
        </Collapse>
      )}
    </Card>
  )
}
